// src/features/posteingang/components/PosteingangPage.tsx
//
// Posteingang: eingescannte Post des Dienstleisters, automatisch abgeholt vom
// Abholjob post-abholung-worker. Sichtbar nur für Mitarbeiter mit dem Merkmal
// darf_posteingang_sehen; die Berechtigung prüft die Netlify-Funktion
// posteingang. Tages- und Monatsreports werden hier nicht angezeigt.
//
// Seit 30.10.2026 (Freigabe Tim): Gelesene Post ist standardmäßig
// ausgeblendet und lässt sich über "Gelesene anzeigen" einblenden. Briefe
// lassen sich einzeln oder als Auswahl an lohn@xtax.de senden, damit daraus
// ein Ticket entsteht. Die Auswahl wird Brief für Brief gesendet; ein
// gesendeter Brief gilt als gelesen. Seit 02.11.2026 lässt sich die Auswahl
// außerdem gesammelt als gelesen markieren.

import React, { useEffect, useMemo, useState } from 'react';
import { FileText, Loader, AlertTriangle, ChevronDown, ChevronRight, Check, Circle, Send } from 'lucide-react';
import { posteingangService, PosteingangFehler } from '../services/posteingangService';
import { PosteingangDatei, PosteingangListe, PosteingangVersandstand } from '../types';

interface Gruppe {
  schluessel: string;
  titel: string;
  dateien: PosteingangDatei[];
}

interface Massenstand {
  art: 'senden' | 'gelesen';
  fertig: number;
  gesamt: number;
}

const formatLieferdatum = (lieferdatum: string): string => {
  const [jahr, monat, tag] = lieferdatum.split('-').map(Number);
  const d = new Date(jahr, monat - 1, tag);
  return d.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

const formatZeitpunkt = (iso: string): string => {
  const d = new Date(iso);
  const datum = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const uhrzeit = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `${datum}, ${uhrzeit} Uhr`;
};

const formatGroesse = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} Byte`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
};

// Ein Brief lässt sich nur einmal an lohn@ senden
const kannSenden = (datei: PosteingangDatei): boolean => !datei.weitergeleitet_am;

export const PosteingangPage: React.FC = () => {
  const [liste, setListe] = useState<PosteingangListe | null>(null);
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [oeffnetGerade, setOeffnetGerade] = useState<string | null>(null);
  const [markiertGerade, setMarkiertGerade] = useState<string | null>(null);
  const [zeigeGelesene, setZeigeGelesene] = useState(false);
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set());
  const [sendetGerade, setSendetGerade] = useState<string | null>(null);
  const [massenstand, setMassenstand] = useState<Massenstand | null>(null);

  useEffect(() => {
    let abgebrochen = false;
    const laden = async () => {
      setLoading(true);
      setFehler(null);
      try {
        const daten = await posteingangService.ladeListe();
        if (!abgebrochen) setListe(daten);
      } catch (error) {
        if (!abgebrochen) {
          setFehler(error instanceof Error ? error.message : String(error));
        }
      } finally {
        if (!abgebrochen) setLoading(false);
      }
    };
    laden();
    return () => {
      abgebrochen = true;
    };
  }, []);

  // Gelesene Post ist ausgeblendet, solange "Gelesene anzeigen" aus ist
  const sichtbar = useMemo((): PosteingangDatei[] => {
    if (!liste) return [];
    return zeigeGelesene ? liste.dateien : liste.dateien.filter(d => !d.gelesen_am);
  }, [liste, zeigeGelesene]);

  const gruppen = useMemo((): Gruppe[] => {
    const map = new Map<string, Gruppe>();
    for (const datei of sichtbar) {
      const schluessel = datei.lieferdatum || `ohne-datum:${datei.ordner}`;
      if (!map.has(schluessel)) {
        map.set(schluessel, {
          schluessel,
          titel: datei.lieferdatum
            ? formatLieferdatum(datei.lieferdatum)
            : `Ohne Lieferdatum${datei.ordner ? ` (Ordner ${datei.ordner})` : ''}`,
          dateien: []
        });
      }
      map.get(schluessel)!.dateien.push(datei);
    }
    return Array.from(map.values());
  }, [sichtbar]);

  // Ausgewählt zählen nur sichtbare Briefe, die noch gesendet werden können
  const ausgewaehlt = useMemo(
    () => sichtbar.filter(d => auswahl.has(d.id) && kannSenden(d)),
    [sichtbar, auswahl]
  );

  // Ungelesene Briefe der Auswahl für "Als gelesen markieren"
  const ausgewaehltUngelesen = useMemo(
    () => ausgewaehlt.filter(d => !d.gelesen_am),
    [ausgewaehlt]
  );

  const versendetBeschaeftigt = massenstand !== null || sendetGerade !== null;

  const uebernehmeVersandstand = (id: string, stand: PosteingangVersandstand) => {
    setListe(vorher => vorher
      ? { ...vorher, dateien: vorher.dateien.map(d => (d.id === id ? { ...d, ...stand } : d)) }
      : vorher);
    setAuswahl(vorher => {
      if (!vorher.has(id)) return vorher;
      const neu = new Set(vorher);
      neu.delete(id);
      return neu;
    });
  };

  const handleOeffnen = async (datei: PosteingangDatei) => {
    // Den Tab sofort im Klick öffnen, sonst blockiert der Browser ihn als Pop-up
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    setOeffnetGerade(datei.id);
    try {
      const url = await posteingangService.oeffnungsLink(datei.id);
      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, '_blank', 'noopener');
      }
    } catch (error) {
      if (tab) tab.close();
      const meldung = error instanceof Error ? error.message : String(error);
      alert(`Die Datei konnte nicht geöffnet werden: ${meldung}`);
      if (error instanceof PosteingangFehler && error.neuAnmelden) {
        setFehler(meldung);
      }
    } finally {
      setOeffnetGerade(null);
    }
  };

  // Lesestand setzen oder zurücknehmen; öffnet bewusst nicht die PDF
  const handleGelesen = async (datei: PosteingangDatei, event: React.MouseEvent) => {
    event.stopPropagation();
    if (markiertGerade) return;
    setMarkiertGerade(datei.id);
    try {
      const stand = await posteingangService.setzeGelesen(datei.id, !datei.gelesen_am);
      setListe(vorher => vorher
        ? { ...vorher, dateien: vorher.dateien.map(d => (d.id === datei.id ? { ...d, ...stand } : d)) }
        : vorher);
    } catch (error) {
      const meldung = error instanceof Error ? error.message : String(error);
      alert(`Der Lesestand konnte nicht gespeichert werden: ${meldung}`);
      if (error instanceof PosteingangFehler && error.neuAnmelden) {
        setFehler(meldung);
      }
    } finally {
      setMarkiertGerade(null);
    }
  };

  // Einzelnen Brief an lohn@ senden; öffnet bewusst nicht die PDF
  const handleSenden = async (datei: PosteingangDatei, event: React.MouseEvent) => {
    event.stopPropagation();
    if (versendetBeschaeftigt || !kannSenden(datei)) return;
    setSendetGerade(datei.id);
    try {
      const stand = await posteingangService.sendeAnLohn(datei.id);
      uebernehmeVersandstand(datei.id, stand);
    } catch (error) {
      const meldung = error instanceof Error ? error.message : String(error);
      alert(`Das Schreiben konnte nicht an lohn@ gesendet werden: ${meldung}`);
      if (error instanceof PosteingangFehler && error.neuAnmelden) {
        setFehler(meldung);
      }
    } finally {
      setSendetGerade(null);
    }
  };

  // Auswahl Brief für Brief senden, damit keine Zeitgrenze greift und Fehler
  // je Brief sichtbar bleiben
  const handleMassenversand = async () => {
    if (versendetBeschaeftigt) return;
    const briefe = ausgewaehlt;
    if (briefe.length === 0) return;
    const bestaetigt = window.confirm(
      `${briefe.length} Schreiben einzeln an lohn@xtax.de senden? Für jedes Schreiben entsteht ein eigenes Ticket.`
    );
    if (!bestaetigt) return;

    const fehlgeschlagen: string[] = [];
    let gesendet = 0;
    setMassenstand({ art: 'senden', fertig: 0, gesamt: briefe.length });
    for (let i = 0; i < briefe.length; i++) {
      const datei = briefe[i];
      try {
        const stand = await posteingangService.sendeAnLohn(datei.id);
        uebernehmeVersandstand(datei.id, stand);
        gesendet++;
      } catch (error) {
        const meldung = error instanceof Error ? error.message : String(error);
        fehlgeschlagen.push(`${datei.dateiname}: ${meldung}`);
        if (error instanceof PosteingangFehler && error.neuAnmelden) {
          setFehler(meldung);
          break;
        }
      }
      setMassenstand({ art: 'senden', fertig: i + 1, gesamt: briefe.length });
    }
    setMassenstand(null);

    if (fehlgeschlagen.length > 0) {
      alert(`${gesendet} von ${briefe.length} Schreiben an lohn@ gesendet.\n\nNicht gesendet:\n${fehlgeschlagen.join('\n')}`);
    }
  };

  // Ungelesene Briefe der Auswahl Brief für Brief als gelesen markieren
  const handleMassenGelesen = async () => {
    if (versendetBeschaeftigt) return;
    const briefe = ausgewaehltUngelesen;
    if (briefe.length === 0) return;

    const fehlgeschlagen: string[] = [];
    let markiert = 0;
    setMassenstand({ art: 'gelesen', fertig: 0, gesamt: briefe.length });
    for (let i = 0; i < briefe.length; i++) {
      const datei = briefe[i];
      try {
        const stand = await posteingangService.setzeGelesen(datei.id, true);
        setListe(vorher => vorher
          ? { ...vorher, dateien: vorher.dateien.map(d => (d.id === datei.id ? { ...d, ...stand } : d)) }
          : vorher);
        setAuswahl(vorher => {
          if (!vorher.has(datei.id)) return vorher;
          const neu = new Set(vorher);
          neu.delete(datei.id);
          return neu;
        });
        markiert++;
      } catch (error) {
        const meldung = error instanceof Error ? error.message : String(error);
        fehlgeschlagen.push(`${datei.dateiname}: ${meldung}`);
        if (error instanceof PosteingangFehler && error.neuAnmelden) {
          setFehler(meldung);
          break;
        }
      }
      setMassenstand({ art: 'gelesen', fertig: i + 1, gesamt: briefe.length });
    }
    setMassenstand(null);

    if (fehlgeschlagen.length > 0) {
      alert(`${markiert} von ${briefe.length} Schreiben als gelesen markiert.\n\nNicht markiert:\n${fehlgeschlagen.join('\n')}`);
    }
  };

  const toggleAuswahl = (id: string) => {
    setAuswahl(vorher => {
      const neu = new Set(vorher);
      if (neu.has(id)) neu.delete(id);
      else neu.add(id);
      return neu;
    });
  };

  // Alle sendbaren Briefe eines Liefertags aus- oder abwählen
  const toggleGruppe = (gruppe: Gruppe) => {
    const ids = gruppe.dateien.filter(kannSenden).map(d => d.id);
    if (ids.length === 0) return;
    setAuswahl(vorher => {
      const neu = new Set(vorher);
      const alleGewaehlt = ids.every(id => neu.has(id));
      for (const id of ids) {
        if (alleGewaehlt) neu.delete(id);
        else neu.add(id);
      }
      return neu;
    });
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px 0' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
          Posteingang
        </h1>
        {liste && (
          <p style={{ margin: '6px 0 0 0', fontSize: '14px', color: '#6B7280' }}>
            {liste.letzte_erfolgreiche_abholung
              ? `Zuletzt erfolgreich abgeholt: ${formatZeitpunkt(liste.letzte_erfolgreiche_abholung)}`
              : 'Bisher keine erfolgreiche Abholung'}
          </p>
        )}
      </div>

      {/* Warnung, wenn der letzte Lauf gescheitert ist */}
      {liste?.letzter_lauf?.status === 'fehler' && (
        <div style={{
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-start',
          padding: '12px 16px',
          marginBottom: '20px',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: '10px',
          color: '#991B1B',
          fontSize: '14px'
        }}>
          <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: '1px' }} />
          <div>
            <div style={{ fontWeight: '600' }}>
              Die letzte Abholung am {formatZeitpunkt(liste.letzter_lauf.beendet_am || liste.letzter_lauf.gestartet_am)} ist fehlgeschlagen.
            </div>
            {liste.letzter_lauf.fehler && (
              <div style={{ marginTop: '4px', whiteSpace: 'pre-wrap' }}>{liste.letzter_lauf.fehler}</div>
            )}
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          padding: '40px',
          color: '#6B7280'
        }}>
          <Loader size={20} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} />
          Laden...
        </div>
      )}

      {/* Fehler beim Laden */}
      {!loading && fehler && (
        <div style={{
          padding: '16px',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: '10px',
          color: '#991B1B',
          fontSize: '14px'
        }}>
          {fehler}
        </div>
      )}

      {/* Ansicht und Massenaktion */}
      {!loading && !fehler && liste && liste.dateien.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap',
          marginBottom: '20px'
        }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#374151', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={zeigeGelesene}
              onChange={(e) => setZeigeGelesene(e.target.checked)}
            />
            Gelesene anzeigen
          </label>

          {(ausgewaehlt.length > 0 || massenstand) && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', color: '#374151' }}>
              {massenstand ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                  <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  {massenstand.art === 'senden' ? 'Sende' : 'Markiere'} {Math.min(massenstand.fertig + 1, massenstand.gesamt)} von {massenstand.gesamt} …
                </span>
              ) : (
                <>
                  <span>{ausgewaehlt.length} ausgewählt</span>
                  <button
                    type="button"
                    onClick={handleMassenversand}
                    disabled={versendetBeschaeftigt}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '6px 12px',
                      backgroundColor: '#3B82F6',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      cursor: versendetBeschaeftigt ? 'wait' : 'pointer'
                    }}
                  >
                    <Send size={13} />
                    An lohn@ senden
                  </button>
                  {ausgewaehltUngelesen.length > 0 && (
                    <button
                      type="button"
                      onClick={handleMassenGelesen}
                      disabled={versendetBeschaeftigt}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '6px 12px',
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#374151',
                        cursor: versendetBeschaeftigt ? 'wait' : 'pointer'
                      }}
                    >
                      <Check size={13} color="#6B7280" />
                      Als gelesen markieren
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setAuswahl(new Set())}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#FFFFFF',
                      border: '1px solid #E5E7EB',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#374151',
                      cursor: 'pointer'
                    }}
                  >
                    Auswahl aufheben
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Keine Post */}
      {!loading && !fehler && liste && liste.dateien.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: '#9CA3AF',
          fontSize: '14px'
        }}>
          Noch keine Post abgeholt.
        </div>
      )}

      {/* Keine ungelesene Post */}
      {!loading && !fehler && liste && liste.dateien.length > 0 && sichtbar.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '40px 20px',
          color: '#9CA3AF',
          fontSize: '14px'
        }}>
          Keine ungelesene Post.
        </div>
      )}

      {/* Post nach Lieferdatum */}
      {!loading && !fehler && gruppen.map(gruppe => {
        const sendbar = gruppe.dateien.filter(kannSenden);
        const alleGewaehlt = sendbar.length > 0 && sendbar.every(d => auswahl.has(d.id));
        return (
          <div key={gruppe.schluessel} style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              {sendbar.length > 0 && (
                <input
                  type="checkbox"
                  checked={alleGewaehlt}
                  disabled={versendetBeschaeftigt}
                  onChange={() => toggleGruppe(gruppe)}
                  title="Alle Schreiben dieses Tages auswählen"
                  style={{ margin: 0 }}
                />
              )}
              <span>
                {gruppe.titel}
                <span style={{ fontSize: '13px', fontWeight: '400', color: '#6B7280', marginLeft: '8px' }}>
                  {gruppe.dateien.length} {gruppe.dateien.length === 1 ? 'Dokument' : 'Dokumente'}
                </span>
              </span>
            </h2>
            <div style={{
              border: '1px solid #E5E7EB',
              borderRadius: '10px',
              overflow: 'hidden',
              backgroundColor: '#FFFFFF'
            }}>
              {gruppe.dateien.map((datei, index) => {
                const oeffnet = oeffnetGerade === datei.id;
                const gelesen = !!datei.gelesen_am;
                const markiert = markiertGerade === datei.id;
                const sendet = sendetGerade === datei.id;
                const sendbarDatei = kannSenden(datei);
                return (
                  <div
                    key={datei.id}
                    onClick={() => { if (!oeffnet) handleOeffnen(datei); }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      borderBottom: index < gruppe.dateien.length - 1 ? '1px solid #F3F4F6' : 'none',
                      cursor: oeffnet ? 'wait' : 'pointer',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F9FAFB'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#FFFFFF'; }}
                    title="PDF in neuem Tab öffnen"
                  >
                    <input
                      type="checkbox"
                      checked={sendbarDatei && auswahl.has(datei.id)}
                      disabled={!sendbarDatei || versendetBeschaeftigt}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleAuswahl(datei.id)}
                      title={sendbarDatei ? 'Für den Versand an lohn@ auswählen' : 'Bereits an lohn@ gesendet'}
                      style={{ margin: 0, flexShrink: 0 }}
                    />

                    {oeffnet
                      ? <Loader size={18} color="#3B82F6" style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
                      : <FileText size={18} color="#3B82F6" style={{ flexShrink: 0 }} />}

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: gelesen ? '500' : '700',
                        color: '#111827',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {!gelesen && (
                          <span
                            title="Ungelesen"
                            style={{
                              display: 'inline-block',
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: '#3B82F6',
                              marginRight: '8px',
                              verticalAlign: 'middle'
                            }}
                          />
                        )}
                        {datei.dateiname}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6B7280' }}>
                        {formatGroesse(datei.groesse)} · abgeholt {formatZeitpunkt(datei.abgeholt_am)}
                      </div>
                      <PostTexterkennung datei={datei} />
                    </div>

                    {datei.fassung > 1 && (
                      <div style={{
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: '#FFFBEB',
                        color: '#D97706',
                        flexShrink: 0
                      }}>
                        Fassung {datei.fassung}
                      </div>
                    )}

                    {datei.weitergeleitet_am ? (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        title={`An lohn@ gesendet am ${formatZeitpunkt(datei.weitergeleitet_am)}${datei.weitergeleitet_von ? ` von ${datei.weitergeleitet_von}` : ''}`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '500',
                          backgroundColor: '#ECFDF5',
                          color: '#047857',
                          flexShrink: 0,
                          cursor: 'default'
                        }}
                      >
                        <Check size={13} />
                        An lohn@ gesendet
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => handleSenden(datei, e)}
                        disabled={versendetBeschaeftigt}
                        title="Schreiben als Mail mit Anhang an lohn@xtax.de senden, daraus entsteht ein Ticket"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 10px',
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                          fontSize: '12px',
                          color: '#374151',
                          cursor: versendetBeschaeftigt ? 'wait' : 'pointer',
                          flexShrink: 0
                        }}
                      >
                        {sendet
                          ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} />
                          : <Send size={13} color="#6B7280" />}
                        An lohn@ senden
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={(e) => handleGelesen(datei, e)}
                      disabled={markiert}
                      title={gelesen && datei.gelesen_am
                        ? `Als gelesen markiert am ${formatZeitpunkt(datei.gelesen_am)}${datei.gelesen_von ? ` von ${datei.gelesen_von}` : ''}`
                        : 'Noch nicht als gelesen markiert'}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '4px 10px',
                        backgroundColor: '#FFFFFF',
                        border: '1px solid #E5E7EB',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#374151',
                        cursor: markiert ? 'wait' : 'pointer',
                        flexShrink: 0
                      }}
                    >
                      {markiert
                        ? <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} />
                        : (gelesen ? <Circle size={13} color="#6B7280" /> : <Check size={13} color="#6B7280" />)}
                      {gelesen ? 'Als ungelesen markieren' : 'Als gelesen markieren'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================
// Zusammenfassung und erkannter Text eines Briefs
// ============================================

// Zeigt unter dem Brief die Zusammenfassung (höchstens 500 Zeichen) aus der
// Texterkennung (post-texterkennung-worker, seit 19.10.2026). Der vollständige
// erkannte Text ist zugeklappt und wird erst beim Aufklappen geladen, damit
// die Liste schnell bleibt. Klicks in diesem Bereich öffnen nicht die PDF,
// damit sich Text markieren und kopieren lässt.
const TEXT_STATUS_HINWEIS: Record<string, string> = {
  offen: 'Texterkennung ausstehend',
  laufend: 'Text wird erkannt …',
  kein_text: 'Kein Text erkennbar',
  zu_gross: 'Zu groß für die Texterkennung',
  fehlgeschlagen: 'Texterkennung fehlgeschlagen'
};

const PostTexterkennung: React.FC<{ datei: PosteingangDatei }> = ({ datei }) => {
  const [offen, setOffen] = useState(false);
  const [laedt, setLaedt] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [gekuerzt, setGekuerzt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const info = datei.texterkennung;
  if (!info || info.status === 'nicht_unterstuetzt') return null;

  // Hinweis, solange keine Zusammenfassung vorliegt. Ist der Text schon
  // erkannt, fehlt nur noch die Zusammenfassung.
  let hinweis: string | null = null;
  if (!info.zusammenfassung) {
    if (info.hat_text) {
      hinweis = info.status === 'fehlgeschlagen' ? 'Zusammenfassung fehlgeschlagen' : 'Zusammenfassung ausstehend';
    } else {
      hinweis = TEXT_STATUS_HINWEIS[info.status] ?? null;
    }
  }

  const handleToggle = async () => {
    if (offen) {
      setOffen(false);
      return;
    }
    setOffen(true);
    if (text !== null || laedt) return;
    setLaedt(true);
    setFehler(null);
    try {
      const ergebnis = await posteingangService.ladeText(datei.id);
      setText(ergebnis.text);
      setGekuerzt(ergebnis.gekuerzt);
    } catch (error) {
      setFehler(error instanceof Error ? error.message : 'Der Text konnte nicht geladen werden.');
    } finally {
      setLaedt(false);
    }
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '6px', cursor: 'default', minWidth: 0 }}
    >
      {info.zusammenfassung && (
        <div style={{
          fontSize: '13px',
          lineHeight: '1.5',
          color: '#374151',
          whiteSpace: 'normal',
          wordBreak: 'break-word'
        }}>
          {info.zusammenfassung}
        </div>
      )}
      {hinweis && (
        <span
          title={info.letzter_fehler ?? undefined}
          style={{ fontSize: '12px', color: '#9CA3AF' }}
        >
          {hinweis}
        </span>
      )}
      {info.hat_text && (
        <>
          <button
            type="button"
            onClick={handleToggle}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '0',
              background: 'none',
              border: 'none',
              fontSize: '12px',
              color: '#6B7280',
              cursor: 'pointer',
              width: 'fit-content'
            }}
          >
            {offen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
            <FileText size={13} />
            Erkannter Text
          </button>
          {offen && (
            <div style={{
              maxHeight: '320px',
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: '12px',
              lineHeight: '1.5',
              color: '#374151',
              backgroundColor: '#FFFFFF',
              border: '1px solid #E5E7EB',
              borderRadius: '6px',
              padding: '8px 10px'
            }}>
              {laedt && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#6B7280' }}>
                  <Loader size={13} style={{ animation: 'spin 1s linear infinite' }} />
                  Lade Text …
                </span>
              )}
              {!laedt && fehler && (
                <span style={{ color: '#B91C1C' }}>{fehler}</span>
              )}
              {!laedt && !fehler && text !== null && (
                <>
                  {(gekuerzt || info.gekuerzt) && (
                    <div style={{ color: '#92400E', marginBottom: '6px' }}>
                      Der Text ist sehr lang und wurde nur teilweise gespeichert.
                    </div>
                  )}
                  {text}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};
